/**
 * Shared time formatting utilities for class-related components
 * Consolidates duplicate time formatting logic across:
 * - ClassMetadata
 * - BrowseClassCard
 * - StudioClassCard
 * - StudioClassTable
 * - PhotoClassCard
 */

/**
 * Safely parse a YYYY-MM-DD date string into a local-midnight Date object.
 *
 * CRITICAL: `new Date("2026-03-12")` treats the string as UTC midnight,
 * which shifts the displayed date one day earlier in US timezones.
 * This function appends "T00:00:00" so the date is interpreted as local midnight.
 *
 * Use this for ANY database DATE column (class.date, booking_date, class_date, etc.).
 * Do NOT use for full ISO timestamps (created_at, updated_at) — those already have time info.
 */
export function parseLocalDate(dateStr: string): Date {
  // If it already has a "T" (full ISO timestamp), parse as-is
  if (dateStr.includes("T")) {
    return new Date(dateStr);
  }
  return new Date(dateStr + "T00:00:00");
}

/**
 * Format a time string (HH:MM) for display (e.g., "6:30PM")
 * @param time Time string in HH:MM format
 * @returns Formatted time string like "6:30PM" or "12PM"
 */
export function formatClassTime(time: string): string {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const min = parseInt(minutes, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  const displayMin = min > 0 ? `:${min.toString().padStart(2, "0")}` : "";
  return `${displayHour}${displayMin}${period}`;
}

/**
 * Format a time range for display (e.g., "6:30PM - 8:30PM")
 * @param startTime Start time in HH:MM format
 * @param endTime End time in HH:MM format
 * @returns Formatted time range string
 */
export function formatClassTimeRange(startTime: string, endTime: string): string {
  return `${formatClassTime(startTime)} - ${formatClassTime(endTime)}`;
}

/**
 * Format a time range compactly without spaces (e.g., "6:30PM-8:30PM")
 * Used in series cards where space is limited.
 * @param startTime Start time in HH:MM format
 * @param endTime End time in HH:MM format
 * @returns Formatted compact time range string
 */
export function formatClassTimeRangeCompact(startTime: string, endTime: string): string {
  return `${formatClassTime(startTime)} - ${formatClassTime(endTime)}`;
}

/**
 * Format a date as compact month + day (e.g., "MAR 5")
 * Used in series cards for per-class schedule display.
 * @param dateStr Date string in YYYY-MM-DD format
 * @returns Uppercase formatted date like "MAR 5"
 */
export function formatSeriesDateCompact(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase();
}

/**
 * Format a date and time for compact display (e.g., "Mon 1/14 6:30PM-8:30PM")
 * @param date Date string in YYYY-MM-DD format or Date object
 * @param startTime Start time in HH:MM format
 * @param endTime End time in HH:MM format
 * @returns Formatted date and time string
 */
export function formatClassDateTime(
  date: string | Date,
  startTime: string,
  endTime: string
): string {
  const dateObj = typeof date === "string" ? new Date(date + "T00:00:00") : date;
  const dayName = dateObj.toLocaleDateString("en-US", { weekday: "short" });
  const month = dateObj.getMonth() + 1;
  const day = dateObj.getDate();
  return `${dayName} ${month}/${day} ${formatClassTime(startTime)}-${formatClassTime(endTime)}`;
}

/**
 * Format a date for display with day of week (e.g., "Monday, January 14")
 * @param date Date string in YYYY-MM-DD format or Date object
 * @returns Formatted date string
 */
export function formatClassDate(date: string | Date): string {
  const dateObj = typeof date === "string" ? new Date(date + "T00:00:00") : date;
  return dateObj.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/**
 * Format a date compactly (e.g., "Mon 1/14")
 * @param date Date string in YYYY-MM-DD format or Date object
 * @returns Formatted compact date string
 */
export function formatClassDateCompact(date: string | Date): string {
  const dateObj = typeof date === "string" ? new Date(date + "T00:00:00") : date;
  const dayName = dateObj.toLocaleDateString("en-US", { weekday: "short" });
  const month = dateObj.getMonth() + 1;
  const day = dateObj.getDate();
  return `${dayName} ${month}/${day}`;
}

/**
 * Check if a class has ended based on the class timezone
 * @param date Class date in YYYY-MM-DD format
 * @param endTime Class end time in HH:MM or HH:MM:SS format
 * @param timezone IANA timezone string (e.g., "America/Los_Angeles")
 * @returns True if the class end time has passed in the class timezone
 */
export function hasClassEnded(date: string, endTime: string, timezone: string): boolean {
  try {
    // Get current date/time in the class's timezone
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // Parse current time parts in class timezone
    const parts = formatter.formatToParts(now);
    const currentYear = parts.find((p) => p.type === "year")?.value;
    const currentMonth = parts.find((p) => p.type === "month")?.value;
    const currentDay = parts.find((p) => p.type === "day")?.value;
    const currentHour = parts.find((p) => p.type === "hour")?.value;
    const currentMinute = parts.find((p) => p.type === "minute")?.value;

    const currentDateStr = `${currentYear}-${currentMonth}-${currentDay}`;
    const currentTimeStr = `${currentHour}:${currentMinute}`;

    // Normalize endTime to HH:MM format (strip seconds if present)
    const normalizedEndTime = endTime.substring(0, 5);

    // Compare class date with current date first
    if (date < currentDateStr) return true;
    if (date > currentDateStr) return false;

    // Same day, compare times
    return normalizedEndTime <= currentTimeStr;
  } catch {
    // Fallback: compare without timezone awareness
    const classEnd = new Date(`${date}T${endTime}`);
    return classEnd < new Date();
  }
}

/**
 * Calculate duration between two times and format for display (e.g., "1hr 30min")
 * @param startTime Start time in HH:MM format
 * @param endTime End time in HH:MM format
 * @returns Formatted duration string
 */
export function formatClassDuration(startTime: string, endTime: string): string {
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);

  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;

  // Handle classes that span midnight
  let durationMinutes = endTotalMinutes - startTotalMinutes;
  if (durationMinutes < 0) {
    durationMinutes += 24 * 60;
  }

  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  if (hours === 0) {
    return `${minutes}MIN`;
  } else if (minutes === 0) {
    return `${hours}HR`;
  } else {
    return `${hours}HR ${minutes}MIN`;
  }
}
